import { useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Upload, FileText, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  COMPANY_DOC_CATEGORIES, CompanyDocCategory, useCompanyDocuments,
} from '@/hooks/useCompanyDocuments';

interface OrderOption {
  order_id: string;
  order_number: string;
}

interface VehicleOption {
  id: string;
  label: string;
}

interface DriverOption {
  id: string;
  label: string;
}

type Scope = 'company' | 'order' | 'vehicle' | 'driver';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  companyName: string;
  orders: OrderOption[];
  vehicles?: VehicleOption[];
  drivers?: DriverOption[];
  isTransporter?: boolean;
  defaultOrderId?: string | null;
  defaultVehicleId?: string | null;
  defaultDriverId?: string | null;
}

const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPTED = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

export const AddCompanyDocumentDialog = ({
  open, onOpenChange, companyId, companyName, orders, vehicles = [], drivers = [], isTransporter = false,
  defaultOrderId = null, defaultVehicleId = null, defaultDriverId = null,
}: Props) => {
  const { t } = useTranslation();
  const { uploadDocument, isUploading } = useCompanyDocuments(companyId);

  const initialScope: Scope = defaultOrderId
    ? 'order'
    : defaultVehicleId
      ? 'vehicle'
      : defaultDriverId
        ? 'driver'
        : 'company';

  const [scope, setScope] = useState<Scope>(initialScope);
  const [orderId, setOrderId] = useState<string>(defaultOrderId || '');
  const [vehicleId, setVehicleId] = useState<string>(defaultVehicleId || '');
  const [driverId, setDriverId] = useState<string>(defaultDriverId || '');
  const [orderSearch, setOrderSearch] = useState('');
  const [resourceSearch, setResourceSearch] = useState('');
  const [category, setCategory] = useState<CompanyDocCategory>('kyc');
  const [title, setTitle] = useState('');
  const [expiry, setExpiry] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const reset = () => {
    setScope(initialScope);
    setOrderId(defaultOrderId || '');
    setVehicleId(defaultVehicleId || '');
    setDriverId(defaultDriverId || '');
    setOrderSearch('');
    setResourceSearch('');
    setCategory('kyc');
    setTitle('');
    setExpiry('');
    setNotes('');
    setFile(null);
    setPreview(null);
  };

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      toast.error(t('adminDocuments.add.invalidType'));
      return;
    }
    if (f.size > MAX_SIZE) {
      toast.error(t('adminDocuments.add.tooLarge'));
      return;
    }
    setFile(f);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const submit = async () => {
    if (!file) {
      toast.error(t('adminDocuments.add.fileRequired'));
      return;
    }
    if (scope === 'order' && !orderId) {
      toast.error(t('adminDocuments.add.orderRequired'));
      return;
    }
    if (scope === 'vehicle' && !vehicleId) {
      toast.error(t('adminDocuments.add.vehicleRequired'));
      return;
    }
    if (scope === 'driver' && !driverId) {
      toast.error(t('adminDocuments.add.driverRequired'));
      return;
    }
    try {
      await uploadDocument({
        companyId,
        orderId: scope === 'order' ? orderId : null,
        vehicleId: scope === 'vehicle' ? vehicleId : null,
        driverId: scope === 'driver' ? driverId : null,
        category,
        title,
        expiryDate: expiry ? new Date(expiry).toISOString() : null,
        notes,
        file,
      });
      toast.success(t('adminDocuments.add.success'));
      reset();
      onOpenChange(false);
    } catch (err) {
      console.error('Company document upload error:', err);
      toast.error(t('adminDocuments.add.error'));
    }
  };

  const filteredOrders = orderSearch
    ? orders.filter((o) => o.order_number.toLowerCase().includes(orderSearch.toLowerCase()))
    : orders;

  const rs = resourceSearch.toLowerCase();
  const filteredVehicles = rs ? vehicles.filter((v) => v.label.toLowerCase().includes(rs)) : vehicles;
  const filteredDrivers = rs ? drivers.filter((d) => d.label.toLowerCase().includes(rs)) : drivers;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Lexend_Deca'] text-[#37699E]">
            {t('adminDocuments.add.title')}
          </DialogTitle>
          <DialogDescription className="font-['Funnel_Sans']">
            {t('adminDocuments.add.description', { company: companyName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Scope */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              {t('adminDocuments.add.scope')}
            </label>
            <Select value={scope} onValueChange={(v) => setScope(v as Scope)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company">{t('adminDocuments.add.scopeCompany')}</SelectItem>
                <SelectItem value="order">{t('adminDocuments.add.scopeOrder')}</SelectItem>
                {(isTransporter || vehicles.length > 0) && (
                  <SelectItem value="vehicle">{t('adminDocuments.add.scopeVehicle')}</SelectItem>
                )}
                {(isTransporter || drivers.length > 0) && (
                  <SelectItem value="driver">{t('adminDocuments.add.scopeDriver')}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {scope === 'order' && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('adminDocuments.add.order')}
              </label>
              <SearchInput
                placeholder={t('adminDocuments.add.orderSearch')}
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
              />
              <Select value={orderId} onValueChange={setOrderId}>
                <SelectTrigger><SelectValue placeholder={t('adminDocuments.add.orderPlaceholder')} /></SelectTrigger>
                <SelectContent>
                  {filteredOrders.map((o) => (
                    <SelectItem key={o.order_id} value={o.order_id}>{o.order_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {scope === 'vehicle' && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('adminDocuments.add.vehicle')}
              </label>
              {vehicles.length === 0 ? (
                <p className="text-sm text-[#F2AC57]">{t('adminDocuments.add.noVehicles')}</p>
              ) : (
                <>
                  <SearchInput
                    placeholder={t('adminDocuments.add.vehicleSearch')}
                    value={resourceSearch}
                    onChange={(e) => setResourceSearch(e.target.value)}
                  />
                  <Select value={vehicleId} onValueChange={setVehicleId}>
                    <SelectTrigger><SelectValue placeholder={t('adminDocuments.add.vehiclePlaceholder')} /></SelectTrigger>
                    <SelectContent>
                      {filteredVehicles.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          )}

          {scope === 'driver' && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('adminDocuments.add.driver')}
              </label>
              {drivers.length === 0 ? (
                <p className="text-sm text-[#F2AC57]">{t('adminDocuments.add.noDrivers')}</p>
              ) : (
                <>
                  <SearchInput
                    placeholder={t('adminDocuments.add.driverSearch')}
                    value={resourceSearch}
                    onChange={(e) => setResourceSearch(e.target.value)}
                  />
                  <Select value={driverId} onValueChange={setDriverId}>
                    <SelectTrigger><SelectValue placeholder={t('adminDocuments.add.driverPlaceholder')} /></SelectTrigger>
                    <SelectContent>
                      {filteredDrivers.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          )}

          {/* Category */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              {t('adminDocuments.add.category')}
            </label>
            <Select value={category} onValueChange={(v) => setCategory(v as CompanyDocCategory)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {COMPANY_DOC_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{t(`adminDocuments.categories.${c}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              {t('adminDocuments.add.label')}
            </label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('adminDocuments.add.labelPlaceholder')} />
          </div>

          {/* Expiry */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              {t('adminDocuments.add.expiry')}
            </label>
            <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              {t('adminDocuments.add.notes')}
            </label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="resize-none" />
          </div>

          {/* File */}
          <div className="space-y-2">
            <input
              type="file"
              id="company-doc-file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={handleFile}
              className="hidden"
              disabled={isUploading}
            />
            <label htmlFor="company-doc-file" className="cursor-pointer block">
              <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#DBE2EA] rounded-lg hover:border-[#0880AE] transition-colors bg-[#EBF4F8] hover:bg-white">
                <Upload size={20} className="text-[#0880AE]" />
                <span className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">
                  {file ? file.name : t('adminDocuments.add.chooseFile')}
                </span>
              </div>
            </label>
            <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">{t('adminDocuments.add.fileHint')}</p>

            {file && (
              <div className="flex items-start gap-4 p-3 border border-[#DBE2EA] rounded-lg">
                {preview ? (
                  <img src={preview} alt={file.name} className="w-16 h-16 object-cover rounded-lg" />
                ) : (
                  <div className="w-16 h-16 bg-[#EBF4F8] rounded-lg flex items-center justify-center">
                    <FileText size={24} className="text-[#0880AE]" />
                  </div>
                )}
                <div className="flex-1 text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                  <p className="text-sm font-semibold text-[#2D3748]">{file.name}</p>
                  <p>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                {!isUploading && (
                  <Button variant="ghost" size="icon" onClick={() => { setFile(null); setPreview(null); }}>
                    <X size={16} />
                  </Button>
                )}
              </div>
            )}

            {isUploading && (
              <div className="space-y-2">
                <Progress value={66} className="h-2" />
                <p className="text-xs text-[#5A6B7A]">{t('adminDocuments.add.uploading')}</p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isUploading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={isUploading}
            className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold"
          >
            {t('adminDocuments.add.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
